import { useCallback } from 'react'
import { useAuthStore } from '@/stores/auth'
import {
  signIn as apiSignIn,
  signUp as apiSignUp,
  signOut as apiSignOut,
  refreshAccessToken,
  getCurrentUser,
} from '@/api/auth'
import { mergeAnonSession } from '@/api/sessions'
import { useSessionStore } from '@/stores/session'

export function useAuth() {
  const user = useAuthStore((state) => state.user)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const isLoading = useAuthStore((state) => state.isLoading)
  const setUser = useAuthStore((state) => state.setUser)
  const clearAuth = useAuthStore((state) => state.clearAuth)
  const setLoading = useAuthStore((state) => state.setLoading)

  const anonSessionId = useSessionStore((state) => state.anonSessionId)
  const loadSessions = useSessionStore((state) => state.loadSessions)
  const setActiveSession = useSessionStore((state) => state.setActiveSession)
  const setAnonSessionId = useSessionStore((state) => state.setAnonSessionId)

  const restoreSession = useCallback(async () => {
    setLoading(true)
    try {
      const { accessToken } = await refreshAccessToken()
      const user = await getCurrentUser()
      setUser(user, accessToken)
    } catch {
      // Stay anonymous when there is no valid refresh cookie.
    } finally {
      setLoading(false)
    }
  }, [setLoading, setUser])

  const signIn = useCallback(
    async (email: string, password: string) => {
      const { user, accessToken } = await apiSignIn(email, password)
      setUser(user, accessToken)
      if (anonSessionId) {
        try {
          await mergeAnonSession(anonSessionId)
          setAnonSessionId(null)
        } catch {
          // Non-fatal if merge fails.
        }
      }
    },
    [anonSessionId, setAnonSessionId, setUser],
  )

  const signUp = useCallback(
    async (email: string, password: string, displayName: string) => {
      const { user, accessToken } = await apiSignUp(email, password, displayName)
      setUser(user, accessToken)
      if (anonSessionId) {
        try {
          await mergeAnonSession(anonSessionId)
          setAnonSessionId(null)
        } catch {
          // Non-fatal if merge fails.
        }
      }
    },
    [anonSessionId, setAnonSessionId, setUser],
  )

  const signOut = useCallback(async () => {
    await apiSignOut()
    clearAuth()
    loadSessions([])
    setActiveSession('')
  }, [clearAuth, loadSessions, setActiveSession])

  return {
    user,
    isAuthenticated,
    isLoading,
    restoreSession,
    signIn,
    signUp,
    signOut,
  }
}
